import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Sa2ResponseArea } from "../data/api/soil/types";
import {
  LRFProjectType,
  mapFarmProjectTypeToLRFProjectType,
  PlantationProjectType,
  SoilProjectType,
  VegProjectType
} from "../data/types/sum";
import { Polygon, LatLng } from "../data/api/types";
import { AUcenter } from "../components/Map/mapConstants";
import {
  checkCompletelyInsideNsw,
  checkCompletelyInsideVic,
  LocationInACT,
  LocationInNewSouthWales,
  LocationInVictoria
} from "../data/api/plantation/types";

export type InQld = {
  completelyInsideQld: boolean;
  percentageInsideQld: number;
};

export enum ExcludedArea {
  Forest = "Forest",
  ClearedNativeForest = "Cleared Native Forest",
  Wetlands = "Wetlands",
  DrainedWetlands = "Drained Wetlands",
  Roads = "Roads",
  Settlements = "Settlements",
  Other = "Other"
}

export const ExcludedAreaDescription = new Map<ExcludedArea, string>([
  [ExcludedArea.Forest, "Forest"],
  [
    ExcludedArea.ClearedNativeForest,
    "Native forest that has been cleared in the past 5 years"
  ],
  [ExcludedArea.Wetlands, "Wetlands"],
  [
    ExcludedArea.DrainedWetlands,
    "Wetlands that have been drained in the past 5 years"
  ],
  [ExcludedArea.Roads, "Roads"],
  [ExcludedArea.Settlements, "Settlements"],
  [
    ExcludedArea.Other,
    "Other (e.g. land otherwise unable to support plant cover)"
  ]
]);

export enum PriorProduction {
  Crop = "Crop",
  Cotton = "Cotton",
  Vegetables = "Vegetables",
  Pasture = "Pasture",
  SugerCane = "Sugar Cane",
  Horticulture = "Horticulture",
  NativeForest = "Native Forest",
  PlantationForest = "Plantation Forest"
}

export enum MapHelpMessage {
  Init = "Click area tool to start drawing evaluation area",
  DrawPolygon = "Click/tap the points around the boundary of your land area",
  CompletePolygon = "Evaluation area is defined, delete to redraw",
  EditPolygon = "Click anywhere outside area to exit edit mode",
  UnchoosePolygon = "Click the area to choose for delete or edit"
}

export const PriorProductionDescription = new Map<PriorProduction, string>([
  [PriorProduction.Crop, "Crop"],
  [PriorProduction.Cotton, "Cotton"],
  [PriorProduction.Vegetables, "Vegetables"],
  [PriorProduction.Pasture, "Pasture"],
  [PriorProduction.SugerCane, "Sugar Cane"],
  [PriorProduction.Horticulture, "Horticulture"],
  [PriorProduction.NativeForest, "Native Forest"],
  [PriorProduction.PlantationForest, "Plantation Forest"]
]);

export interface ExcludedAreas {
  areas: Array<ExcludedArea>;
  landPercentage: number | null;
}

export interface VegProjectObject {
  [VegProjectType.AvoidedClearingNativeVegetation]: boolean;
  [VegProjectType.EnvironmentalMalleePlanting]: boolean;
  [VegProjectType.HumanInducedRegrowth]: boolean;
  [VegProjectType.NativeForestManagedRegrowth]: boolean;
}
export interface SoilProjectObject {
  [SoilProjectType.AcidityManagementNewIrrigation]: boolean;
  [SoilProjectType.AcidityManagementPastureRenovation]: boolean;
  [SoilProjectType.ConversionToPasture]: boolean;
  [SoilProjectType.NewIrrigationPastureRenovation]: boolean;
  [SoilProjectType.NutrientManagementAcidityManagement]: boolean;
  [SoilProjectType.NutrientManagementNewIrrigation]: boolean;
  [SoilProjectType.NutrientManagementPastureRenovation]: boolean;
  [SoilProjectType.StubbleRetention]: boolean;
  [SoilProjectType.CarbonSoilSequestration]: boolean;
  [SoilProjectType.BeefHerdManagement]: boolean;
}

export interface PlantationProjectObject {
  [PlantationProjectType.PlantationForestActivites]: boolean;
  [PlantationProjectType.ShortToLongRotation]: boolean;
  [PlantationProjectType.ContinuingRotationalHarvest]: boolean;
  [PlantationProjectType.TransitioningToPermanentForest]: boolean;
  [PlantationProjectType.FarmingForestry]: boolean;
  [PlantationProjectType.EstablishingNewPlantationForest]: boolean;
}

export interface LRFProjectObject {
  [LRFProjectType.AvoidedDeforestation]: boolean;
  [LRFProjectType.EnvironmentalPlantings]: boolean;
  [LRFProjectType.Revegetation]: boolean;
  [LRFProjectType.Soil]: boolean;
}

export interface FarmDetailsState {
  address?: string;
  polygons: Array<Polygon>;
  zoom?: number;
  center: LatLng;
  excludedAreas: ExcludedAreas;
  previousProduction: PriorProduction | null;
  irrigation: boolean | null;
  fertiliser: boolean | null;
  lime: boolean | null;
  pastureRenovation: boolean | null;
  plantationRotation: boolean | null;
  stubbleRevoval: boolean | null;
  irrigationDuringCarbon: boolean | null;
  sa2s: Array<Sa2ResponseArea<number>>;
  appliableVegProjectTypes: VegProjectObject;
  appliableSoilProjectTypes: SoilProjectObject;
  appliablePlantationProjectTypes: PlantationProjectObject;
  appliableLRFProjectTypes: LRFProjectObject;
  inQld: InQld | null;
  inVic: LocationInVictoria | null;
  inNsw: LocationInNewSouthWales | null;
  inAct: LocationInACT | null;
}

export const INITIAL_STATE: FarmDetailsState = {
  polygons: [],
  zoom: 3,
  center: AUcenter,
  excludedAreas: { areas: [], landPercentage: null },
  irrigation: null,
  irrigationDuringCarbon: null,
  previousProduction: null,
  plantationRotation: null,
  pastureRenovation: null,
  fertiliser: null,
  lime: null,
  stubbleRevoval: null,
  sa2s: [],
  appliableVegProjectTypes: {
    [VegProjectType.AvoidedClearingNativeVegetation]: false,
    [VegProjectType.EnvironmentalMalleePlanting]: false,
    [VegProjectType.HumanInducedRegrowth]: false,
    [VegProjectType.NativeForestManagedRegrowth]: false
  },
  appliableSoilProjectTypes: {
    [SoilProjectType.AcidityManagementNewIrrigation]: false,
    [SoilProjectType.AcidityManagementPastureRenovation]: false,
    [SoilProjectType.ConversionToPasture]: false,
    [SoilProjectType.NewIrrigationPastureRenovation]: false,
    [SoilProjectType.NutrientManagementAcidityManagement]: false,
    [SoilProjectType.NutrientManagementNewIrrigation]: false,
    [SoilProjectType.NutrientManagementPastureRenovation]: false,
    [SoilProjectType.StubbleRetention]: false,
    [SoilProjectType.CarbonSoilSequestration]: false,
    [SoilProjectType.BeefHerdManagement]: false
  },
  appliablePlantationProjectTypes: {
    [PlantationProjectType.PlantationForestActivites]: false,
    [PlantationProjectType.FarmingForestry]: false,
    [PlantationProjectType.EstablishingNewPlantationForest]: false,
    [PlantationProjectType.ShortToLongRotation]: false,
    [PlantationProjectType.ContinuingRotationalHarvest]: false,
    [PlantationProjectType.TransitioningToPermanentForest]: false
  },
  appliableLRFProjectTypes: {
    [LRFProjectType.AvoidedDeforestation]: false,
    [LRFProjectType.EnvironmentalPlantings]: false,
    [LRFProjectType.Revegetation]: false,
    [LRFProjectType.Soil]: false
  },
  inQld: null,
  inVic: null,
  inNsw: null,
  inAct: null
};

const sortAppliableSoilProjectTypes = (state: FarmDetailsState) => {
  //Veg project sort
  // line 2, 4
  if (state.previousProduction === PriorProduction.NativeForest) {
    state.appliableVegProjectTypes[
      VegProjectType.EnvironmentalMalleePlanting
    ] = false;
    state.appliableVegProjectTypes[
      VegProjectType.AvoidedClearingNativeVegetation
    ] = true;
    state.appliableSoilProjectTypes[
      SoilProjectType.CarbonSoilSequestration
    ] = false;
  } else {
    state.appliableVegProjectTypes[
      VegProjectType.EnvironmentalMalleePlanting
    ] = true;
    state.appliableVegProjectTypes[
      VegProjectType.AvoidedClearingNativeVegetation
    ] = false;
    state.appliableSoilProjectTypes[
      SoilProjectType.CarbonSoilSequestration
    ] = true;
  }

  if (state.previousProduction === PriorProduction.PlantationForest) {
    state.appliablePlantationProjectTypes[
      PlantationProjectType.PlantationForestActivites
    ] = true;

    state.appliableVegProjectTypes[
      VegProjectType.EnvironmentalMalleePlanting
    ] = false;

    if (checkCompletelyInsideVic(state)) {
      // Short to long rotation is only available if short rotation was selected for the prior production
      if (state.plantationRotation) {
        state.appliablePlantationProjectTypes[
          PlantationProjectType.ShortToLongRotation
        ] = true;
      } else {
        state.appliablePlantationProjectTypes[
          PlantationProjectType.ShortToLongRotation
        ] = false;
      }
    }
    if (checkCompletelyInsideNsw(state)) {
      state.appliablePlantationProjectTypes[
        PlantationProjectType.ShortToLongRotation
      ] = true;
    }

    state.appliablePlantationProjectTypes[
      PlantationProjectType.ContinuingRotationalHarvest
    ] = true;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.TransitioningToPermanentForest
    ] = true;
  } else {
    state.appliablePlantationProjectTypes[
      PlantationProjectType.PlantationForestActivites
    ] = false;
  }

  // line 3, 5
  if (state.previousProduction === PriorProduction.Pasture) {
    state.appliableVegProjectTypes[VegProjectType.HumanInducedRegrowth] = true;
    state.appliableVegProjectTypes[
      VegProjectType.NativeForestManagedRegrowth
    ] = true;
    state.appliableSoilProjectTypes[SoilProjectType.BeefHerdManagement] = true;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.FarmingForestry
    ] = true;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.EstablishingNewPlantationForest
    ] = true;
  } else {
    state.appliableVegProjectTypes[VegProjectType.HumanInducedRegrowth] = false;
    state.appliableVegProjectTypes[
      VegProjectType.NativeForestManagedRegrowth
    ] = false;
    state.appliableSoilProjectTypes[SoilProjectType.BeefHerdManagement] = false;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.FarmingForestry
    ] = false;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.EstablishingNewPlantationForest
    ] = false;
  }

  // Soil project sort, keep it simple and adhere to the conditionalityTable.xlsx. Later change of conditions, could update here easily.
  //line 6
  if (state.previousProduction === PriorProduction.NativeForest || state.lime) {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementAcidityManagement
    ] = false;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementAcidityManagement
    ] = true;
  }

  // line 7
  if (
    state.previousProduction === PriorProduction.NativeForest ||
    state.irrigation
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementNewIrrigation
    ] = false;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementNewIrrigation
    ] = true;
  }

  // line 8
  if (
    state.previousProduction === PriorProduction.Pasture &&
    !state.pastureRenovation
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementPastureRenovation
    ] = true;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.NutrientManagementPastureRenovation
    ] = false;
  }

  // line 9
  if (
    state.previousProduction === PriorProduction.NativeForest ||
    state.irrigation ||
    state.lime
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.AcidityManagementNewIrrigation
    ] = false;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.AcidityManagementNewIrrigation
    ] = true;
  }

  // 10
  if (
    state.previousProduction === PriorProduction.Pasture &&
    !state.lime &&
    !state.pastureRenovation
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.AcidityManagementPastureRenovation
    ] = true;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.AcidityManagementPastureRenovation
    ] = false;
  }
  // line 11
  if (
    state.previousProduction === PriorProduction.Pasture &&
    !state.irrigation &&
    !state.pastureRenovation
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.NewIrrigationPastureRenovation
    ] = true;
  } else {
    state.appliableSoilProjectTypes[
      SoilProjectType.NewIrrigationPastureRenovation
    ] = false;
  }

  if (state.previousProduction === PriorProduction.Crop) {
    state.appliablePlantationProjectTypes[
      PlantationProjectType.FarmingForestry
    ] = true;
    state.appliablePlantationProjectTypes[
      PlantationProjectType.EstablishingNewPlantationForest
    ] = true;
  }

  // line 12
  if (
    [
      PriorProduction.Crop,
      PriorProduction.Vegetables,
      PriorProduction.SugerCane,
      PriorProduction.Cotton,
      PriorProduction.Horticulture
    ].indexOf(state.previousProduction as PriorProduction) >= 0 &&
    state.stubbleRevoval
  ) {
    state.appliableSoilProjectTypes[SoilProjectType.StubbleRetention] = true;
  } else {
    state.appliableSoilProjectTypes[SoilProjectType.StubbleRetention] = false;
  }
  // line 13
  if (
    state.previousProduction === PriorProduction.Pasture ||
    state.previousProduction === PriorProduction.NativeForest
  ) {
    state.appliableSoilProjectTypes[
      SoilProjectType.ConversionToPasture
    ] = false;
  } else {
    state.appliableSoilProjectTypes[SoilProjectType.ConversionToPasture] = true;
  }
};

const farmDetailsSlice = createSlice({
  name: "farmDetails",
  initialState: INITIAL_STATE,
  reducers: {
    setAddress(state, action: PayloadAction<string | undefined>) {
      state.address = action.payload;
    },
    addPolygon(state, action: PayloadAction<Polygon>) {
      state.polygons = state.polygons.concat([action.payload]);
    },
    clearPolygons(state, action) {
      state.polygons = [];
    },
    setZoom(state, action: PayloadAction<number | undefined>) {
      state.zoom = action.payload;
    },
    setCenter(state, action: PayloadAction<LatLng>) {
      state.center = action.payload;
    },
    setExcludedAreas(state, action: PayloadAction<ExcludedArea>) {
      if (state.excludedAreas.areas.includes(action.payload)) {
        state.excludedAreas.areas.splice(
          state.excludedAreas.areas.indexOf(action.payload),
          1
        );
      } else {
        state.excludedAreas.areas.push(action.payload);
      }
    },
    setLandPercentage(state, action: PayloadAction<number | null>) {
      state.excludedAreas.landPercentage = action.payload;
    },
    setPreviousProduction(
      state,
      action: PayloadAction<PriorProduction | null>
    ) {
      state.previousProduction = action.payload;
      if (
        [
          PriorProduction.Crop,
          PriorProduction.Cotton,
          PriorProduction.Vegetables,
          PriorProduction.SugerCane,
          PriorProduction.Horticulture
        ].indexOf(state.previousProduction as PriorProduction) < 0
      ) {
        state.stubbleRevoval = null;
      }

      if (state.previousProduction !== PriorProduction.Pasture) {
        state.pastureRenovation = null;
      }

      if (state.previousProduction === PriorProduction.NativeForest) {
        state.irrigation = null;
        state.irrigationDuringCarbon = null;
        state.fertiliser = null;
        state.lime = null;
      }

      if (state.previousProduction === PriorProduction.PlantationForest) {
        state.irrigation = null;
        state.irrigationDuringCarbon = null;
        state.fertiliser = null;
        state.lime = null;
      } else {
        state.plantationRotation = null;
      }
      sortAppliableSoilProjectTypes(state);
    },
    setIrrigation(state, action: PayloadAction<boolean | null>) {
      state.irrigation = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setFertiliser(state, action: PayloadAction<boolean | null>) {
      state.fertiliser = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setLime(state, action: PayloadAction<boolean | null>) {
      state.lime = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setPastureRenovation(state, action: PayloadAction<boolean | null>) {
      state.pastureRenovation = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setPlantationRotation(state, action: PayloadAction<boolean | null>) {
      state.plantationRotation = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setStubbleRevoval(state, action: PayloadAction<boolean | null>) {
      state.stubbleRevoval = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    setIrrigationDuringCarbon(state, action: PayloadAction<boolean | null>) {
      state.irrigationDuringCarbon = action.payload;
      sortAppliableSoilProjectTypes(state);
    },
    appendSa2(state, action: PayloadAction<Array<Sa2ResponseArea<number>>>) {
      state.sa2s = state.sa2s.concat(action.payload);
    },
    setSa2s(state, action: PayloadAction<Array<Sa2ResponseArea<number>>>) {
      state.sa2s = action.payload;
    },
    setInQld(state, action: PayloadAction<InQld | null>) {
      state.inQld = action.payload;
    },
    setInVic(state, action: PayloadAction<LocationInVictoria | null>) {
      state.inVic = action.payload;
    },
    setInNsw(state, action: PayloadAction<LocationInNewSouthWales | null>) {
      state.inNsw = action.payload;
    },
    setInAct(state, action: PayloadAction<LocationInACT | null>) {
      state.inAct = action.payload;
    },
    sortAppliableLRFProjectTypes(state) {
      // update LRF related ones
      Object.entries(state.appliableVegProjectTypes).forEach(
        ([vegProjectTypeStr, value]) => {
          const lrfProjectType = mapFarmProjectTypeToLRFProjectType(
            vegProjectTypeStr as VegProjectType
          );
          if (lrfProjectType) {
            state.appliableLRFProjectTypes[lrfProjectType] = value;
          }
        }
      );

      Object.entries(state.appliableSoilProjectTypes).forEach(
        ([soilProjectTypeStr, value]) => {
          const lrfProjectType = mapFarmProjectTypeToLRFProjectType(
            soilProjectTypeStr as SoilProjectType
          );
          if (lrfProjectType) {
            state.appliableLRFProjectTypes[lrfProjectType] = value;
          }
        }
      );
    }
  }
});

// Extract the action creators object and the reducer
const { actions, reducer } = farmDetailsSlice;
// Extract and export each action creator by name
export const {
  setAddress,
  addPolygon,
  clearPolygons,
  setExcludedAreas,
  setLandPercentage,
  setPreviousProduction,
  setIrrigation,
  setFertiliser,
  setLime,
  setPastureRenovation,
  setPlantationRotation,
  setStubbleRevoval,
  setIrrigationDuringCarbon,
  setSa2s,
  appendSa2,
  setZoom,
  setCenter,
  setInQld,
  setInVic,
  setInNsw,
  setInAct,
  sortAppliableLRFProjectTypes
} = actions;
// Export the reducer, either as a default or named export
export default reducer;
